import InputField from '@cube3/cubicle/src/core/atoms/InputField/InputField';
import React from 'react';
import { getErrorMessage } from '../utils/getErrorMessage';
import { shouldShowErrors } from '../utils/shouldShowErrors';
import { FieldWithState } from '../types/GenericFormTypes';

export type TextFieldProps = {
  multiline?: number;
};

type Props = FieldWithState<string, 'phone_number'>;

export const PhoneField = (props: Props) => {
  const {
    //meta
    error,
    touched,
    active,
    invalid,
    //
    showErrors = 'touched',
    autoComplete,
    ...extraProps
  } = props;

  const errorMessage = error && getErrorMessage(error);

  const shouldError = shouldShowErrors(showErrors, touched, active);

  return (
    <InputField
      {...extraProps}
      inputMode="numeric"
      messageLabel={errorMessage}
      showMessage={errorMessage && shouldError}
      criticalState={invalid && shouldError}
      autoFill={!!autoComplete}
      prefixElement={findFlagForNumber(extraProps.value)?.flag}
    />
  );
};

// some stuff for showing the correct flag with a phone number, based on country code

const PHONE_DATA = [
  {
    short: 'AC',
    full: 'Ascension Island',
    flag: '🇦🇨',
    phone: '247'
  },
  {
    short: 'AD',
    full: 'Andorra',
    flag: '🇦🇩',
    phone: '376'
  },
  {
    short: 'AE',
    full: 'United Arab Emirates',
    flag: '🇦🇪',
    phone: '971'
  },
  {
    short: 'AF',
    full: 'Afghanistan',
    flag: '🇦🇫',
    phone: '93'
  },
  {
    short: 'AG',
    full: 'Antigua & Barbuda',
    flag: '🇦🇬'
  },
  {
    short: 'AI',
    full: 'Anguilla',
    flag: '🇦🇮',
    phone: '1264'
  },
  {
    short: 'AL',
    full: 'Albania',
    flag: '🇦🇱',
    phone: '355'
  },
  {
    short: 'AM',
    full: 'Armenia',
    flag: '🇦🇲',
    phone: '374'
  },
  {
    short: 'AO',
    full: 'Angola',
    flag: '🇦🇴',
    phone: '244'
  },
  {
    short: 'AQ',
    full: 'Antarctica',
    flag: '🇦🇶'
  },
  {
    short: 'AR',
    full: 'Argentina',
    flag: '🇦🇷',
    phone: '54'
  },
  {
    short: 'AS',
    full: 'American Samoa',
    flag: '🇦🇸',
    phone: '1684'
  },
  {
    short: 'AT',
    full: 'Austria',
    flag: '🇦🇹',
    phone: '43'
  },
  {
    short: 'AU',
    full: 'Australia',
    flag: '🇦🇺',
    phone: '61'
  },
  {
    short: 'AW',
    full: 'Aruba',
    flag: '🇦🇼',
    phone: '297'
  },
  {
    short: 'AX',
    full: 'Åland Islands',
    flag: '🇦🇽'
  },
  {
    short: 'AZ',
    full: 'Azerbaijan',
    flag: '🇦🇿',
    phone: '994'
  },
  {
    short: 'BA',
    full: 'Bosnia & Herzegovina',
    flag: '🇧🇦'
  },
  {
    short: 'BB',
    full: 'Barbados',
    flag: '🇧🇧',
    phone: '1246'
  },
  {
    short: 'BD',
    full: 'Bangladesh',
    flag: '🇧🇩',
    phone: '880'
  },
  {
    short: 'BE',
    full: 'Belgium',
    flag: '🇧🇪',
    phone: '32'
  },
  {
    short: 'BF',
    full: 'Burkina Faso',
    flag: '🇧🇫',
    phone: '226'
  },
  {
    short: 'BG',
    full: 'Bulgaria',
    flag: '🇧🇬',
    phone: '359'
  },
  {
    short: 'BH',
    full: 'Bahrain',
    flag: '🇧🇭',
    phone: '973'
  },
  {
    short: 'BI',
    full: 'Burundi',
    flag: '🇧🇮',
    phone: '257'
  },
  {
    short: 'BJ',
    full: 'Benin',
    flag: '🇧🇯',
    phone: '229'
  },
  {
    short: 'BL',
    full: 'St. Barthélemy',
    flag: '🇧🇱'
  },
  {
    short: 'BM',
    full: 'Bermuda',
    flag: '🇧🇲',
    phone: '1441'
  },
  {
    short: 'BN',
    full: 'Brunei',
    flag: '🇧🇳',
    phone: '673'
  },
  {
    short: 'BO',
    full: 'Bolivia',
    flag: '🇧🇴',
    phone: '591'
  },
  {
    short: 'BQ',
    full: 'Caribbean Netherlands',
    flag: '🇧🇶'
  },
  {
    short: 'BR',
    full: 'Brazil',
    flag: '🇧🇷',
    phone: '55'
  },
  {
    short: 'BS',
    full: 'Bahamas',
    flag: '🇧🇸',
    phone: '1242'
  },
  {
    short: 'BT',
    full: 'Bhutan',
    flag: '🇧🇹',
    phone: '975'
  },
  {
    short: 'BV',
    full: 'Bouvet Island',
    flag: '🇧🇻'
  },
  {
    short: 'BW',
    full: 'Botswana',
    flag: '🇧🇼',
    phone: '267'
  },
  {
    short: 'BY',
    full: 'Belarus',
    flag: '🇧🇾',
    phone: '375'
  },
  {
    short: 'BZ',
    full: 'Belize',
    flag: '🇧🇿',
    phone: '501'
  },
  {
    short: 'CA',
    full: 'Canada',
    flag: '🇨🇦',
    phone: '1',
    areacodes: [
      403, 587, 825, 368, 780, 587, 825, 368, 604, 778, 236, 672, 250, 778, 236,
      672, 204, 431, 584, 506, 428, 709, 867, 902, 867, 905, 289, 365, 742, 519,
      226, 548, 705, 249, 683, 613, 343, 753, 807, 416, 647, 437, 902, 418, 581,
      367, 450, 579, 354, 514, 438, 263, 819, 873, 468, 306, 639, 474, 867
    ]
  },
  {
    short: 'CC',
    full: 'Cocos (Keeling Islands)'
  },
  {
    short: 'CD',
    full: 'Congo-Kinshasa',
    flag: '🇨🇩'
  },
  {
    short: 'CF',
    full: 'Central African Republic',
    flag: '🇨🇫',
    phone: '236'
  },
  {
    short: 'CG',
    full: 'Congo-Brazzaville',
    flag: '🇨🇬'
  },
  {
    short: 'CH',
    full: 'Switzerland',
    flag: '🇨🇭',
    phone: '41'
  },
  {
    short: 'CI',
    full: "Côte d'Ivoire"
  },
  {
    short: 'CK',
    full: 'Cook Islands',
    flag: '🇨🇰',
    phone: '682'
  },
  {
    short: 'CL',
    full: 'Chile',
    flag: '🇨🇱',
    phone: '56'
  },
  {
    short: 'CM',
    full: 'Cameroon',
    flag: '🇨🇲',
    phone: '237'
  },
  {
    short: 'CN',
    full: 'China',
    flag: '🇨🇳',
    phone: '86'
  },
  {
    short: 'CO',
    full: 'Colombia',
    flag: '🇨🇴',
    phone: '57'
  },
  {
    short: 'CP',
    full: 'Clipperton Island',
    flag: '🇨🇵'
  },
  {
    short: 'CQ',
    full: 'Sark',
    flag: '🇨🇶'
  },
  {
    short: 'CR',
    full: 'Costa Rica',
    flag: '🇨🇷',
    phone: '506'
  },
  {
    short: 'CU',
    full: 'Cuba',
    flag: '🇨🇺',
    phone: '53'
  },
  {
    short: 'CV',
    full: 'Cape Verde',
    flag: '🇨🇻',
    phone: '238'
  },
  {
    short: 'CW',
    full: 'Curaçao',
    flag: '🇨🇼',
    phone: '5999'
  },
  {
    short: 'CX',
    full: 'Christmas Island',
    flag: '🇨🇽',
    phone: '618 9164'
  },
  {
    short: 'CY',
    full: 'Cyprus',
    flag: '🇨🇾',
    phone: '357'
  },
  {
    short: 'CZ',
    full: 'Czechia',
    flag: '🇨🇿'
  },
  {
    short: 'DE',
    full: 'Germany',
    flag: '🇩🇪',
    phone: '49'
  },
  {
    short: 'DG',
    full: 'Diego Garcia',
    flag: '🇩🇬'
  },
  {
    short: 'DJ',
    full: 'Djibouti',
    flag: '🇩🇯',
    phone: '253'
  },
  {
    short: 'DK',
    full: 'Denmark',
    flag: '🇩🇰',
    phone: '45'
  },
  {
    short: 'DM',
    full: 'Dominica',
    flag: '🇩🇲',
    phone: '1767'
  },
  {
    short: 'DO',
    full: 'Dominican Republic',
    flag: '🇩🇴',
    phone: '1809, 829, 849'
  },
  {
    short: 'DZ',
    full: 'Algeria',
    flag: '🇩🇿',
    phone: '213'
  },
  {
    short: 'EA',
    full: 'Ceuta & Melilla',
    flag: '🇪🇦'
  },
  {
    short: 'EC',
    full: 'Ecuador',
    flag: '🇪🇨',
    phone: '593'
  },
  {
    short: 'EE',
    full: 'Estonia',
    flag: '🇪🇪',
    phone: '372'
  },
  {
    short: 'EG',
    full: 'Egypt',
    flag: '🇪🇬',
    phone: '20'
  },
  {
    short: 'EH',
    full: 'Western Sahara',
    flag: '🇪🇭',
    phone: '212'
  },
  {
    short: 'ER',
    full: 'Eritrea',
    flag: '🇪🇷',
    phone: '291'
  },
  {
    short: 'ES',
    full: 'Spain',
    flag: '🇪🇸',
    phone: '34'
  },
  {
    short: 'ET',
    full: 'Ethiopia',
    flag: '🇪🇹',
    phone: '251'
  },
  {
    short: 'EU',
    full: 'European Union',
    flag: '🇪🇺'
  },
  {
    short: 'FI',
    full: 'Finland',
    flag: '🇫🇮',
    phone: '358'
  },
  {
    short: 'FJ',
    full: 'Fiji',
    flag: '🇫🇯',
    phone: '679'
  },
  {
    short: 'FK',
    full: 'Falkland Islands',
    flag: '🇫🇰',
    phone: '500'
  },
  {
    short: 'FM',
    full: 'Micronesia',
    flag: '🇫🇲',
    phone: '691'
  },
  {
    short: 'FO',
    full: 'Faroe Islands',
    flag: '🇫🇴',
    phone: '298'
  },
  {
    short: 'FR',
    full: 'France',
    flag: '🇫🇷',
    phone: '33'
  },
  {
    short: 'GA',
    full: 'Gabon',
    flag: '🇬🇦',
    phone: '241'
  },
  {
    short: 'GB',
    full: 'United Kingdom',
    flag: '🇬🇧',
    phone: '44'
  },
  {
    short: 'GD',
    full: 'Grenada',
    flag: '🇬🇩',
    phone: '1473'
  },
  {
    short: 'GE',
    full: 'Georgia',
    flag: '🇬🇪',
    phone: '995'
  },
  {
    short: 'GF',
    full: 'French Guiana',
    flag: '🇬🇫',
    phone: '594'
  },
  {
    short: 'GG',
    full: 'Guernsey',
    flag: '🇬🇬',
    phone: '441481'
  },
  {
    short: 'GH',
    full: 'Ghana',
    flag: '🇬🇭',
    phone: '233'
  },
  {
    short: 'GI',
    full: 'Gibraltar',
    flag: '🇬🇮',
    phone: '350'
  },
  {
    short: 'GL',
    full: 'Greenland',
    flag: '🇬🇱',
    phone: '299'
  },
  {
    short: 'GM',
    full: 'Gambia',
    flag: '🇬🇲',
    phone: '220'
  },
  {
    short: 'GN',
    full: 'Guinea',
    flag: '🇬🇳',
    phone: '224'
  },
  {
    short: 'GP',
    full: 'Guadeloupe',
    flag: '🇬🇵',
    phone: '590'
  },
  {
    short: 'GQ',
    full: 'Equatorial Guinea',
    flag: '🇬🇶',
    phone: '240'
  },
  {
    short: 'GR',
    full: 'Greece',
    flag: '🇬🇷',
    phone: '30'
  },
  {
    short: 'GS',
    full: 'South Georgia & South Sandwich Islands',
    flag: '🇬🇸'
  },
  {
    short: 'GT',
    full: 'Guatemala',
    flag: '🇬🇹',
    phone: '502'
  },
  {
    short: 'GU',
    full: 'Guam',
    flag: '🇬🇺',
    phone: '1671'
  },
  {
    short: 'GW',
    full: 'Guinea-Bissau',
    flag: '🇬🇼',
    phone: '245'
  },
  {
    short: 'GY',
    full: 'Guyana',
    flag: '🇬🇾',
    phone: '592'
  },
  {
    short: 'HK',
    full: 'Hong Kong SAR China',
    flag: '🇭🇰'
  },
  {
    short: 'HM',
    full: 'Heard & McDonald Islands',
    flag: '🇭🇲'
  },
  {
    short: 'HN',
    full: 'Honduras',
    flag: '🇭🇳',
    phone: '504'
  },
  {
    short: 'HR',
    full: 'Croatia',
    flag: '🇭🇷',
    phone: '385'
  },
  {
    short: 'HT',
    full: 'Haiti',
    flag: '🇭🇹',
    phone: '509'
  },
  {
    short: 'HU',
    full: 'Hungary',
    flag: '🇭🇺',
    phone: '36'
  },
  {
    short: 'IC',
    full: 'Canary Islands',
    flag: '🇮🇨'
  },
  {
    short: 'ID',
    full: 'Indonesia',
    flag: '🇮🇩',
    phone: '62'
  },
  {
    short: 'IE',
    full: 'Ireland',
    flag: '🇮🇪',
    phone: '353'
  },
  {
    short: 'IL',
    full: 'Israel',
    flag: '🇮🇱',
    phone: '972'
  },
  {
    short: 'IM',
    full: 'Isle of Man',
    flag: '🇮🇲',
    phone: '441624'
  },
  {
    short: 'IN',
    full: 'India',
    flag: '🇮🇳',
    phone: '91'
  },
  {
    short: 'IO',
    full: 'British Indian Ocean Territory',
    flag: '🇮🇴',
    phone: '246'
  },
  {
    short: 'IQ',
    full: 'Iraq',
    flag: '🇮🇶',
    phone: '964'
  },
  {
    short: 'IR',
    full: 'Iran',
    flag: '🇮🇷',
    phone: '98'
  },
  {
    short: 'IS',
    full: 'Iceland',
    flag: '🇮🇸',
    phone: '354'
  },
  {
    short: 'IT',
    full: 'Italy',
    flag: '🇮🇹',
    phone: '39'
  },
  {
    short: 'JE',
    full: 'Jersey',
    flag: '🇯🇪',
    phone: '441534'
  },
  {
    short: 'JM',
    full: 'Jamaica',
    flag: '🇯🇲',
    phone: '1658, 876'
  },
  {
    short: 'JO',
    full: 'Jordan',
    flag: '🇯🇴',
    phone: '962'
  },
  {
    short: 'JP',
    full: 'Japan',
    flag: '🇯🇵',
    phone: '81'
  },
  {
    short: 'KE',
    full: 'Kenya',
    flag: '🇰🇪',
    phone: '254'
  },
  {
    short: 'KG',
    full: 'Kyrgyzstan',
    flag: '🇰🇬',
    phone: '996'
  },
  {
    short: 'KH',
    full: 'Cambodia',
    flag: '🇰🇭',
    phone: '855'
  },
  {
    short: 'KI',
    full: 'Kiribati',
    flag: '🇰🇮',
    phone: '686'
  },
  {
    short: 'KM',
    full: 'Comoros',
    flag: '🇰🇲',
    phone: '269'
  },
  {
    short: 'KN',
    full: 'St. Kitts & Nevis',
    flag: '🇰🇳'
  },
  {
    short: 'KP',
    full: 'North Korea',
    flag: '🇰🇵',
    phone: '850'
  },
  {
    short: 'KR',
    full: 'South Korea',
    flag: '🇰🇷',
    phone: '82'
  },
  {
    short: 'KW',
    full: 'Kuwait',
    flag: '🇰🇼',
    phone: '965'
  },
  {
    short: 'KY',
    full: 'Cayman Islands',
    flag: '🇰🇾',
    phone: '1345'
  },
  {
    short: 'KZ',
    full: 'Kazakhstan',
    flag: '🇰🇿',
    phone: '997'
  },
  {
    short: 'LA',
    full: 'Laos',
    flag: '🇱🇦',
    phone: '856'
  },
  {
    short: 'LB',
    full: 'Lebanon',
    flag: '🇱🇧',
    phone: '961'
  },
  {
    short: 'LC',
    full: 'St. Lucia',
    flag: '🇱🇨'
  },
  {
    short: 'LI',
    full: 'Liechtenstein',
    flag: '🇱🇮',
    phone: '423'
  },
  {
    short: 'LK',
    full: 'Sri Lanka',
    flag: '🇱🇰',
    phone: '94'
  },
  {
    short: 'LR',
    full: 'Liberia',
    flag: '🇱🇷',
    phone: '231'
  },
  {
    short: 'LS',
    full: 'Lesotho',
    flag: '🇱🇸',
    phone: '266'
  },
  {
    short: 'LT',
    full: 'Lithuania',
    flag: '🇱🇹',
    phone: '370'
  },
  {
    short: 'LU',
    full: 'Luxembourg',
    flag: '🇱🇺',
    phone: '352'
  },
  {
    short: 'LV',
    full: 'Latvia',
    flag: '🇱🇻',
    phone: '371'
  },
  {
    short: 'LY',
    full: 'Libya',
    flag: '🇱🇾',
    phone: '218'
  },
  {
    short: 'MA',
    full: 'Morocco',
    flag: '🇲🇦',
    phone: '212'
  },
  {
    short: 'MC',
    full: 'Monaco',
    flag: '🇲🇨',
    phone: '377'
  },
  {
    short: 'MD',
    full: 'Moldova',
    flag: '🇲🇩',
    phone: '373'
  },
  {
    short: 'ME',
    full: 'Montenegro',
    flag: '🇲🇪',
    phone: '382'
  },
  {
    short: 'MF',
    full: 'St. Martin',
    flag: '🇲🇫'
  },
  {
    short: 'MG',
    full: 'Madagascar',
    flag: '🇲🇬',
    phone: '261'
  },
  {
    short: 'MH',
    full: 'Marshall Islands',
    flag: '🇲🇭',
    phone: '692'
  },
  {
    short: 'MK',
    full: 'North Macedonia',
    flag: '🇲🇰',
    phone: '389'
  },
  {
    short: 'ML',
    full: 'Mali',
    flag: '🇲🇱',
    phone: '223'
  },
  {
    short: 'MM',
    full: 'Myanmar (Burma)',
    flag: '🇲🇲'
  },
  {
    short: 'MN',
    full: 'Mongolia',
    flag: '🇲🇳',
    phone: '976'
  },
  {
    short: 'MO',
    full: 'Macao SAR China',
    flag: '🇲🇴'
  },
  {
    short: 'MP',
    full: 'Northern Mariana Islands',
    flag: '🇲🇵',
    phone: '1670'
  },
  {
    short: 'MQ',
    full: 'Martinique',
    flag: '🇲🇶',
    phone: '596'
  },
  {
    short: 'MR',
    full: 'Mauritania',
    flag: '🇲🇷',
    phone: '222'
  },
  {
    short: 'MS',
    full: 'Montserrat',
    flag: '🇲🇸',
    phone: '1664'
  },
  {
    short: 'MT',
    full: 'Malta',
    flag: '🇲🇹',
    phone: '356'
  },
  {
    short: 'MU',
    full: 'Mauritius',
    flag: '🇲🇺',
    phone: '230'
  },
  {
    short: 'MV',
    full: 'Maldives',
    flag: '🇲🇻',
    phone: '960'
  },
  {
    short: 'MW',
    full: 'Malawi',
    flag: '🇲🇼',
    phone: '265'
  },
  {
    short: 'MX',
    full: 'Mexico',
    flag: '🇲🇽',
    phone: '52'
  },
  {
    short: 'MY',
    full: 'Malaysia',
    flag: '🇲🇾',
    phone: '60'
  },
  {
    short: 'MZ',
    full: 'Mozambique',
    flag: '🇲🇿',
    phone: '258'
  },
  {
    short: 'NA',
    full: 'Namibia',
    flag: '🇳🇦',
    phone: '264'
  },
  {
    short: 'NC',
    full: 'New Caledonia',
    flag: '🇳🇨',
    phone: '687'
  },
  {
    short: 'NE',
    full: 'Niger',
    flag: '🇳🇪',
    phone: '227'
  },
  {
    short: 'NF',
    full: 'Norfolk Island',
    flag: '🇳🇫',
    phone: '6723'
  },
  {
    short: 'NG',
    full: 'Nigeria',
    flag: '🇳🇬',
    phone: '234'
  },
  {
    short: 'NI',
    full: 'Nicaragua',
    flag: '🇳🇮',
    phone: '505'
  },
  {
    short: 'NL',
    full: 'Netherlands',
    flag: '🇳🇱',
    phone: '31'
  },
  {
    short: 'NO',
    full: 'Norway',
    flag: '🇳🇴',
    phone: '47'
  },
  {
    short: 'NP',
    full: 'Nepal',
    flag: '🇳🇵',
    phone: '977'
  },
  {
    short: 'NR',
    full: 'Nauru',
    flag: '🇳🇷',
    phone: '674'
  },
  {
    short: 'NU',
    full: 'Niue',
    flag: '🇳🇺',
    phone: '683'
  },
  {
    short: 'NZ',
    full: 'New Zealand',
    flag: '🇳🇿',
    phone: '64'
  },
  {
    short: 'OM',
    full: 'Oman',
    flag: '🇴🇲',
    phone: '968'
  },
  {
    short: 'PA',
    full: 'Panama',
    flag: '🇵🇦',
    phone: '507'
  },
  {
    short: 'PE',
    full: 'Peru',
    flag: '🇵🇪',
    phone: '51'
  },
  {
    short: 'PF',
    full: 'French Polynesia',
    flag: '🇵🇫',
    phone: '689'
  },
  {
    short: 'PG',
    full: 'Papua New Guinea',
    flag: '🇵🇬',
    phone: '675'
  },
  {
    short: 'PH',
    full: 'Philippines',
    flag: '🇵🇭',
    phone: '63'
  },
  {
    short: 'PK',
    full: 'Pakistan',
    flag: '🇵🇰',
    phone: '92'
  },
  {
    short: 'PL',
    full: 'Poland',
    flag: '🇵🇱',
    phone: '48'
  },
  {
    short: 'PM',
    full: 'St. Pierre & Miquelon',
    flag: '🇵🇲',
    phone: '508'
  },
  {
    short: 'PN',
    full: 'Pitcairn Islands',
    flag: '🇵🇳',
    phone: '870'
  },
  {
    short: 'PR',
    full: 'Puerto Rico',
    flag: '🇵🇷',
    phone: '1787'
  },
  {
    short: 'PS',
    full: 'Palestinian Territories',
    flag: '🇵🇸'
  },
  {
    short: 'PT',
    full: 'Portugal',
    flag: '🇵🇹',
    phone: '351'
  },
  {
    short: 'PW',
    full: 'Palau',
    flag: '🇵🇼',
    phone: '680'
  },
  {
    short: 'PY',
    full: 'Paraguay',
    flag: '🇵🇾',
    phone: '595'
  },
  {
    short: 'QA',
    full: 'Qatar',
    flag: '🇶🇦',
    phone: '974'
  },
  {
    short: 'RE',
    full: 'Réunion',
    flag: '🇷🇪',
    phone: '262'
  },
  {
    short: 'RO',
    full: 'Romania',
    flag: '🇷🇴',
    phone: '40'
  },
  {
    short: 'RS',
    full: 'Serbia',
    flag: '🇷🇸',
    phone: '381'
  },
  {
    short: 'RU',
    full: 'Russia',
    flag: '🇷🇺',
    phone: '7'
  },
  {
    short: 'RW',
    full: 'Rwanda',
    flag: '🇷🇼',
    phone: '250'
  },
  {
    short: 'SA',
    full: 'Saudi Arabia',
    flag: '🇸🇦',
    phone: '966'
  },
  {
    short: 'SB',
    full: 'Solomon Islands',
    flag: '🇸🇧',
    phone: '677'
  },
  {
    short: 'SC',
    full: 'Seychelles',
    flag: '🇸🇨',
    phone: '248'
  },
  {
    short: 'SD',
    full: 'Sudan',
    flag: '🇸🇩',
    phone: '249'
  },
  {
    short: 'SE',
    full: 'Sweden',
    flag: '🇸🇪',
    phone: '46'
  },
  {
    short: 'SG',
    full: 'Singapore',
    flag: '🇸🇬',
    phone: '65'
  },
  {
    short: 'SH',
    full: 'St. Helena',
    flag: '🇸🇭'
  },
  {
    short: 'SI',
    full: 'Slovenia',
    flag: '🇸🇮',
    phone: '386'
  },
  {
    short: 'SJ',
    full: 'Svalbard & Jan Mayen',
    flag: '🇸🇯'
  },
  {
    short: 'SK',
    full: 'Slovakia',
    flag: '🇸🇰',
    phone: '421'
  },
  {
    short: 'SL',
    full: 'Sierra Leone',
    flag: '🇸🇱',
    phone: '232'
  },
  {
    short: 'SM',
    full: 'San Marino',
    flag: '🇸🇲',
    phone: '295'
  },
  {
    short: 'SN',
    full: 'Senegal',
    flag: '🇸🇳',
    phone: '221'
  },
  {
    short: 'SO',
    full: 'Somalia',
    flag: '🇸🇴',
    phone: '252'
  },
  {
    short: 'SR',
    full: 'Suriname',
    flag: '🇸🇷',
    phone: '597'
  },
  {
    short: 'SS',
    full: 'South Sudan',
    flag: '🇸🇸',
    phone: '211'
  },
  {
    short: 'ST',
    full: 'São Tomé & Príncipe',
    flag: '🇸🇹'
  },
  {
    short: 'SV',
    full: 'El Salvador',
    flag: '🇸🇻',
    phone: '503'
  },
  {
    short: 'SX',
    full: 'Sint Maarten',
    flag: '🇸🇽',
    phone: '1721'
  },
  {
    short: 'SY',
    full: 'Syria',
    flag: '🇸🇾',
    phone: '963'
  },
  {
    short: 'SZ',
    full: 'Eswatini',
    flag: '🇸🇿',
    phone: '268'
  },
  {
    short: 'TA',
    full: 'Tristan da Cunha',
    flag: '🇹🇦',
    phone: '2908'
  },
  {
    short: 'TC',
    full: 'Turks & Caicos Islands',
    flag: '🇹🇨'
  },
  {
    short: 'TD',
    full: 'Chad',
    flag: '🇹🇩',
    phone: '235'
  },
  {
    short: 'TF',
    full: 'French Southern Territories',
    flag: '🇹🇫'
  },
  {
    short: 'TG',
    full: 'Togo',
    flag: '🇹🇬',
    phone: '228'
  },
  {
    short: 'TH',
    full: 'Thailand',
    flag: '🇹🇭',
    phone: '66'
  },
  {
    short: 'TJ',
    full: 'Tajikistan',
    flag: '🇹🇯',
    phone: '992'
  },
  {
    short: 'TK',
    full: 'Tokelau',
    flag: '🇹🇰',
    phone: '690'
  },
  {
    short: 'TL',
    full: 'Timor-Leste',
    flag: '🇹🇱'
  },
  {
    short: 'TM',
    full: 'Turkmenistan',
    flag: '🇹🇲',
    phone: '993'
  },
  {
    short: 'TN',
    full: 'Tunisia',
    flag: '🇹🇳',
    phone: '216'
  },
  {
    short: 'TO',
    full: 'Tonga',
    flag: '🇹🇴',
    phone: '676'
  },
  {
    short: 'TR',
    full: 'Türkiye',
    flag: '🇹🇷'
  },
  {
    short: 'TT',
    full: 'Trinidad & Tobago',
    flag: '🇹🇹'
  },
  {
    short: 'TV',
    full: 'Tuvalu',
    flag: '🇹🇻',
    phone: '688'
  },
  {
    short: 'TW',
    full: 'Taiwan',
    flag: '🇹🇼',
    phone: '886'
  },
  {
    short: 'TZ',
    full: 'Tanzania',
    flag: '🇹🇿',
    phone: '255'
  },
  {
    short: 'UA',
    full: 'Ukraine',
    flag: '🇺🇦',
    phone: '380'
  },
  {
    short: 'UG',
    full: 'Uganda',
    flag: '🇺🇬',
    phone: '256'
  },
  {
    short: 'UM',
    full: 'U.S. Outlying Islands',
    flag: '🇺🇲'
  },
  {
    short: 'UN',
    full: 'United Nations',
    flag: '🇺🇳'
  },
  {
    short: 'US',
    full: 'United States',
    flag: '🇺🇸',
    phone: '1'
  },
  {
    short: 'UY',
    full: 'Uruguay',
    flag: '🇺🇾',
    phone: '598'
  },
  {
    short: 'UZ',
    full: 'Uzbekistan',
    flag: '🇺🇿',
    phone: '998'
  },
  {
    short: 'VA',
    full: 'Vatican City',
    flag: '🇻🇦',
    phone: '379'
  },
  {
    short: 'VC',
    full: 'St. Vincent & Grenadines',
    flag: '🇻🇨'
  },
  {
    short: 'VE',
    full: 'Venezuela',
    flag: '🇻🇪',
    phone: '58'
  },
  {
    short: 'VG',
    full: 'British Virgin Islands',
    flag: '🇻🇬',
    phone: '1284'
  },
  {
    short: 'VI',
    full: 'U.S. Virgin Islands',
    flag: '🇻🇮'
  },
  {
    short: 'VN',
    full: 'Vietnam',
    flag: '🇻🇳',
    phone: '84'
  },
  {
    short: 'VU',
    full: 'Vanuatu',
    flag: '🇻🇺',
    phone: '678'
  },
  {
    short: 'WF',
    full: 'Wallis & Futuna',
    flag: '🇼🇫'
  },
  {
    short: 'WS',
    full: 'Samoa',
    flag: '🇼🇸',
    phone: '685'
  },
  {
    short: 'XK',
    full: 'Kosovo',
    flag: '🇽🇰',
    phone: '383'
  },
  {
    short: 'YE',
    full: 'Yemen',
    flag: '🇾🇪',
    phone: '967'
  },
  {
    short: 'YT',
    full: 'Mayotte',
    flag: '🇾🇹',
    phone: '269'
  },
  {
    short: 'ZA',
    full: 'South Africa',
    flag: '🇿🇦',
    phone: '27'
  },
  {
    short: 'ZM',
    full: 'Zambia',
    flag: '🇿🇲',
    phone: '260'
  },
  {
    short: 'ZW',
    full: 'Zimbabwe',
    flag: '🇿🇼',
    phone: '263'
  }
];

export const findFlagForNumber = (phonenumber: string) => {
  if (!phonenumber) {
    return;
  }
  const normalized = phonenumber
    .replace(/^(00|\+)/, '')
    .replaceAll(/[ -]+/g, '');

  return PHONE_DATA.reduce((acc, val) => {
    if (
      normalized.startsWith(val.phone) &&
      (!acc ||
        acc.phone.length < val.phone.length ||
        (acc.phone === val.phone &&
          val.areacodes?.some((ac) =>
            normalized.startsWith(`${val.phone}${ac}`)
          )) ||
        !acc.areacodes?.some((ac) =>
          normalized.startsWith(`${val.phone}${ac}`)
        ))
    ) {
      return val;
    }
    return acc;
  }, null);
};
